/**
 * Model3DModal - 3D模型查看器Modal组件
 */

import { useState, useEffect, useCallback } from 'react';
import { useLanguage } from '../../context/LanguageContext';
import ModelViewer from './ModelViewer';
import ControlPanel from './ControlPanel';
import LoadingScreen from './LoadingScreen';
import styles from './Model3DModal.module.css';

export default function Model3DModal({
    isOpen,
    onClose,
    modelUrl,
    title,
    // 模型配置
    modelScale,
    modelPosition,
    modelRotation,
    // 相机配置
    cameraHeight,
    cameraDistance,
    cameraLookAtY,
    // 功能配置
    showControlPanel = true,
    enableFullscreen = true,
}) {
    const { t } = useLanguage();
    const [isLoading, setIsLoading] = useState(true);
    const [loadError, setLoadError] = useState(null);
    const [nodeList, setNodeList] = useState([]);
    const [hiddenNodes, setHiddenNodes] = useState([]);
    const [isFullscreen, setIsFullscreen] = useState(false);

    const displayTitle = title || t('viewer3d.title');

    // 重置状态当Modal打开时
    useEffect(() => {
        if (isOpen) {
            setIsLoading(true);
            setLoadError(null);
            setNodeList([]);
            setHiddenNodes([]);
        }
    }, [isOpen]);

    // ESC键关闭
    useEffect(() => {
        const handleKeyDown = (e) => {
            if (e.key === 'Escape' && isOpen) {
                if (isFullscreen) {
                    setIsFullscreen(false);
                } else {
                    onClose();
                }
            }
        };

        window.addEventListener('keydown', handleKeyDown);
        return () => window.removeEventListener('keydown', handleKeyDown);
    }, [isOpen, isFullscreen, onClose]);

    // 禁止背景滚动
    useEffect(() => {
        if (isOpen) {
            document.body.style.overflow = 'hidden';
        } else {
            document.body.style.overflow = '';
        }
        return () => {
            document.body.style.overflow = '';
        };
    }, [isOpen]);

    const handleNodeListReady = useCallback((nodes) => {
        setNodeList(nodes);
        setIsLoading(false);
        setLoadError(null);
    }, []);

    const handleLoadError = useCallback((error) => {
        console.error('Model load error:', error);
        setLoadError(error?.message || t('viewer3d.error.loadFailed'));
        setIsLoading(false);
    }, [t]);

    const handleToggleNode = useCallback((nodeName) => {
        setHiddenNodes(prev =>
            prev.includes(nodeName)
                ? prev.filter(n => n !== nodeName)
                : [...prev, nodeName]
        );
    }, []);

    const handleResetVisibility = useCallback(() => {
        setHiddenNodes([]);
    }, []);

    const toggleFullscreen = useCallback(() => {
        setIsFullscreen(prev => !prev);
    }, []);

    if (!isOpen) return null;

    return (
        <div className={styles.overlay} onClick={onClose}>
            <div
                className={`${styles.modal} ${isFullscreen ? styles.fullscreen : ''}`}
                onClick={(e) => e.stopPropagation()}
            >
                {/* Header */}
                <header className={styles.header}>
                    <h2 className={styles.title}>{displayTitle}</h2>
                    <div className={styles.headerActions}>
                        {enableFullscreen && (
                            <button
                                className={styles.iconBtn}
                                onClick={toggleFullscreen}
                                title={isFullscreen ? t('viewer3d.exitFullscreen') : t('viewer3d.fullscreen')}
                            >
                                {isFullscreen ? '⛶' : '⛶'}
                            </button>
                        )}
                        <button
                            className={styles.closeBtn}
                            onClick={onClose}
                            title={t('viewer3d.close')}
                        >
                            ✕
                        </button>
                    </div>
                </header>

                {/* Content */}
                <div className={styles.content}>
                    {/* 3D Viewer */}
                    <div className={styles.viewerContainer}>
                        {isLoading && !loadError && <LoadingScreen message={t('viewer3d.loading')} />}
                        {loadError && (
                            <div className={styles.errorState}>
                                <span className={styles.errorIcon}>⚠️</span>
                                <h3>{t('viewer3d.error.loadFailed')}</h3>
                                <p>{loadError}</p>
                                <p className={styles.errorHint}>{t('viewer3d.error.checkConnection')}</p>
                            </div>
                        )}
                        {!loadError && (
                            <ModelViewer
                                modelUrl={modelUrl}
                                hiddenNodes={hiddenNodes}
                                onNodeListReady={handleNodeListReady}
                                onError={handleLoadError}
                                modelScale={modelScale}
                                modelPosition={modelPosition}
                                modelRotation={modelRotation}
                                cameraHeight={cameraHeight}
                                cameraDistance={cameraDistance}
                                cameraLookAtY={cameraLookAtY}
                                width="100%"
                                height="100%"
                            />
                        )}
                    </div>

                    {/* Control Panel */}
                    {showControlPanel && !isLoading && !loadError && (
                        <ControlPanel
                            nodeList={nodeList}
                            hiddenNodes={hiddenNodes}
                            onToggleNode={handleToggleNode}
                            onResetVisibility={handleResetVisibility}
                        />
                    )}
                </div>

                {/* Footer */}
                <footer className={styles.footer}>
                    <div className={styles.footerInfo}>
                        <span>{nodeList.length} {t('viewer3d.parts')}</span>
                        {hiddenNodes.length > 0 && (
                            <span className={styles.hiddenCount}>
                                {hiddenNodes.length} {t('viewer3d.hidden')}
                            </span>
                        )}
                    </div>
                    <div className={styles.footerHint}>
                        {t('viewer3d.hint')}
                    </div>
                </footer>
            </div>
        </div>
    );
}
